import type { BreadcrumbProps } from '@blueprintjs/core'
import { makeAutoObservable, toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { setPayload } from '../actions/bulk_actions'
import { setBulkActionProgress as reduxSetBulkActionProgress } from '../actions/progress'
import { PerformanceFilters, TableName } from '../config'
import { useKdpFilters } from '../hooks/useKdpFilters'
import { useUser } from '../hooks/useUser'
import type { AdGroup } from '../types/ad_group'
import { axiosPatch } from '../util/api'
import type { TableProps } from './ad_groups/AdGroupsTable'
import { addExtraCols } from './campaigns/CampaignsTable'
import { Layout } from './Layout'
import {
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
} from './table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable } from './table2/AgGridTable'
import { NegativeKeywordModal } from './ui/BulkActionsModals'
const defaultFilters = [...PerformanceFilters]

const mutableFields: string[] = ['negative_keywords']

export interface SearchTermI {
    type_of: 'search_term' | 'ad_group' | 'keyword' | 'target'
    search_term: string
    ad_group?: AdGroup
    negated?: boolean
    // Flat properties from v5 endpoint
    ad_group_id?: number
    ad_group_name?: string
    ad_group_state?: string
    campaign_id?: number
    campaign_name?: string
    campaign_state?: string
}

class SearchTermStore {
    searchTerm: SearchTermI | null = null
    loading: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    setSearchTerm(searchTerm: SearchTermI | null) {
        console.log('Trying to set search term in', toJS(this), toJS(this?.searchTerm))
        this.searchTerm = searchTerm
    }

    setLoading(loading: boolean) {
        this.loading = loading
    }

    clear() {
        this.searchTerm = null
        this.loading = false
    }
}

export const searchTermStore = new SearchTermStore()

export const SearchTermsTable2 = observer(
    ({
        nested,
        tableHooks,
        store,
        savedFiltersStore,
    }: TableProps<SearchTermI & ExpandedPerformanceMetrics>): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)
        const dispatch = useDispatch()
        const { params } = tableHooks

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return newColumns(useUserValues.is_kdp)
        }, [useUserValues.is_kdp])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.SearchTerm}
                nested={nested}
                params={params}
            >
                <AgGridTable
                    name={TableName.SearchTerm}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    search
                    searchPlaceholder="Search by search term"
                    columnSelection
                    mutableFields={mutableFields}
                    nested={nested}
                />
                <NegativeKeywordModal
                    loading={searchTermStore.loading}
                    searchTerm={searchTermStore.searchTerm}
                    setSearchTerm={(v) => searchTermStore.setSearchTerm(v)}
                    onClose={() => searchTermStore.clear()}
                    reloadParent={tableHooks.reload}
                    onSubmit={(payload) => {
                        dispatch(setPayload(payload))
                        searchTermStore.setLoading(true)

                        return axiosPatch('bulk-actions/update-negative-keywords', payload)
                            .then((res) => {
                                searchTermStore.setLoading(false)
                                dispatch(reduxSetBulkActionProgress(res.data.progress))
                                return res
                            })
                            .catch((err) => {
                                searchTermStore.setLoading(false)
                                throw err
                            })
                    }}
                />
            </Layout>
        )
    },
)

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [Columns.SearchTerm, ...defaultPerfColumns]

    addExtraCols(cols, isKdp)

    return cols.filter((col) => col.field !== 'ideal_cpc' && col.field !== 'target_acos')
}
